import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { action } from './api.ts';
import { device } from './session.tsx';
import type { Place } from './types.ts';

/**
 * The business's locations, and the one this device works at. A till is at its register's
 * location; a waiter's phone or the kitchen screen chooses one. With a single location
 * nothing about places shows anywhere.
 */
interface PlacesState {
  /** Every location, closed ones too (reports still name them). */
  all: Place[];
  /** The open ones, the main one first. */
  places: Place[];
  /** Where this device is: its chosen location, or the main one. Null until loaded. */
  here: Place | null;
  /** More than one open location: screens show where things are. */
  multi: boolean;
  /** Whether a record's location is `id`; records from before locations belong to the first one. */
  at(value: string | null | undefined, id: string | null | undefined): boolean;
  setHere(id: string): void;
  name(id: string | null | undefined): string;
  reload(): Promise<void>;
}

const Ctx = createContext<PlacesState | null>(null);

export function PlacesProvider({ children }: { children: ReactNode }) {
  const [all, setAll] = useState<Place[]>([]);
  const [chosen, setChosen] = useState<string | null>(device.location);
  const reload = useCallback(async () => {
    try {
      setAll(await action<Place[]>('company.locations', {}));
    } catch {
      // Offline: screens work without places, as for a single location.
    }
  }, []);
  useEffect(() => {
    void reload();
  }, [reload]);
  const value = useMemo<PlacesState>(() => {
    const places = all.filter((p) => p.active);
    const main = places.find((p) => p.main) ?? places[0] ?? null;
    const here = places.find((p) => p.id === chosen) ?? main;
    return {
      all,
      places,
      here,
      multi: places.length > 1,
      at: (value, id) => !id || (value ?? all[0]?.id) === id,
      setHere(id: string) {
        device.location = id;
        setChosen(id);
      },
      name: (id) => all.find((p) => p.id === id)?.name ?? '',
      reload,
    };
  }, [all, chosen, reload]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePlaces(): PlacesState {
  const v = useContext(Ctx);
  if (!v) throw new Error('usePlaces outside PlacesProvider');
  return v;
}
